/**
 * Shared DataTable state: view mode, page size, filters, selection restore.
 */

import * as React from 'react';
import { useNavigationType } from 'react-router-dom';
import { useViewMode } from '@/hooks/useViewMode';
import { useFilterVisibility } from '@/hooks/useFilterVisibility';
import { CollapseReplaceHiddenContext } from '@/components/common/CollapseReplaceWrapper';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth, InterfaceElement } from '@/types/interface-elements.types';

/** Page-size map keyed by view mode and layout width. */
type DataTableItemsPerPageMap = Record<ViewMode, Record<ElementWidth, number>>;

/** Options for {@link useDataTable} (table key, filters, expand ownership). */
export interface UseDataTableOptions<TFilters extends object, FID extends string = string> {
  tableName: string;
  defaultFilters: TFilters;
  allFilterGroupIds: readonly FID[];
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  externalViewMode?: ViewMode;
  onExternalViewModeChange?: (mode: ViewMode) => void;
  externalExpandedRowId?: string | null;
  /** When true, the hook owns `expandedRowId` (e.g. {@link PairsDataTable}). */
  manageExpandedRowId?: boolean;
  /** Resolved row for `expandedRowId` — dependency for the expand-scroll effect. */
  expandedItem?: unknown;
  /** Close the open detail panel when the view mode changes. */
  onCloseDetails?: () => void;
  /** Notify the host page when list filters change (calendar-sync seed). */
  onFiltersChange?: (filters: TFilters) => void;
}

/** Shared DataTable state returned by {@link useDataTable}. */
export interface UseDataTableReturn<TFilters extends object, FID extends string = string> {
  viewMode: ViewMode;
  handleViewModeChange: (mode: ViewMode) => void;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  itemsPerPage: number;
  filters: TFilters;
  setFilters: React.Dispatch<React.SetStateAction<TFilters>>;
  /** First-load filter seeds (school year, pinned parent) — does not close the expanded row. */
  primeFilters: React.Dispatch<React.SetStateAction<TFilters>>;
  clearFilters: () => void;
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  showConfigure: boolean;
  setShowConfigure: React.Dispatch<React.SetStateAction<boolean>>;
  filterVisibility: Record<FID, boolean>;
  visibleFilterIds: FID[];
  handleToggleFilterVisibility: (id: FID) => void;
  isSavingFilterVisibility: boolean;
  allowedFilterIds: string[];
  interfaceElements: InterfaceElement[];
  deleteId: string | null;
  setDeleteId: React.Dispatch<React.SetStateAction<string | null>>;
  expandedRowId: string | null;
  setExpandedRowId: (id: string | null) => void;
  closeExpandedRow: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
  expandedRowRef: React.RefObject<HTMLTableRowElement>;
  expandedCollapseRef: React.RefObject<HTMLDivElement>;
}

/** Copy a width row and treat `auto` as `full`. */
function rowWithAuto(
  row: Record<'full' | '1/2' | '2/3' | '1/3', number>,
): Record<ElementWidth, number> {
  return { ...row, auto: row.full };
}

/** Default DataTable page sizes by view mode and layout width. */
const ITEMS_PER_PAGE: DataTableItemsPerPageMap = {
  list: rowWithAuto({ full: 10, '2/3': 5, '1/2': 5, '1/3': 3 }),
  cards: rowWithAuto({ full: 20, '2/3': 5, '1/2': 5, '1/3': 3 }),
};

/** Smaller page sizes when the DataTable is embedded as a dashboard section. */
const ITEMS_PER_PAGE_SECTION: DataTableItemsPerPageMap = {
  list: rowWithAuto({ full: 5, '2/3': 5, '1/2': 5, '1/3': 3 }),
  cards: rowWithAuto({ full: 8, '2/3': 4, '1/2': 4, '1/3': 3 }),
};

/** Page size for the current view mode, layout width, and section vs full table. */
function resolveDataTableItemsPerPage(
  viewMode: ViewMode,
  layoutWidth: ElementWidth,
  isSection: boolean,
): number {
  const width: Exclude<ElementWidth, 'auto'> = layoutWidth === 'auto' ? 'full' : layoutWidth;
  const config = isSection ? ITEMS_PER_PAGE_SECTION : ITEMS_PER_PAGE;
  return config[viewMode][width];
}

/** ISO datetime prefix used when reviving sessionStorage table state. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
/** Revive ISO date strings in persisted DataTable state. */
function dateReviver(_key: string, value: unknown): unknown {
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) return new Date(value);
  return value;
}

/** Read persisted page/filters for a DataTable from sessionStorage. */
function readTableState(tableName: string): { page?: number; filters?: unknown } | null {
  const raw = sessionStorage.getItem(`dt-state:${tableName}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw, dateReviver) as { page?: number; filters?: unknown };
  } catch {
    return null;
  }
}

/**
 * Restores page/filters from sessionStorage on POP, persists `dt-state:`, and closes the detail panel on filter, page, or view-mode change.
 */
export function useDataTable<TFilters extends object, FID extends string = string>(
  options: UseDataTableOptions<TFilters, FID>,
): UseDataTableReturn<TFilters, FID> {
  const {
    tableName,
    defaultFilters,
    allFilterGroupIds,
    layoutWidth: layoutWidthProp = 'full',
    isSection = false,
    externalViewMode,
    onExternalViewModeChange,
    externalExpandedRowId,
    manageExpandedRowId = false,
    expandedItem,
    onCloseDetails,
    onFiltersChange,
  } = options;

  const layoutWidth = layoutWidthProp === 'auto' ? 'full' : layoutWidthProp;

  const navigationType = useNavigationType();

  const { viewMode: persistedViewMode, setViewMode: setPersistedViewMode } = useViewMode(tableName);

  const viewMode = externalViewMode ?? persistedViewMode;
  const setViewMode = onExternalViewModeChange ?? setPersistedViewMode;

  const itemsPerPage = React.useMemo(
    () => resolveDataTableItemsPerPage(viewMode, layoutWidth, isSection),
    [viewMode, layoutWidth, isSection],
  );

  const [currentPage, setCurrentPage] = React.useState<number>(() => {
    if (navigationType === 'POP') {
      return readTableState(tableName)?.page ?? 1;
    }
    return 1;
  });

  const [filters, setFiltersState] = React.useState<TFilters>(() => {
    if (navigationType === 'POP') {
      const saved = readTableState(tableName)?.filters;
      if (saved) return { ...defaultFilters, ...(saved as TFilters) };
    }
    return defaultFilters;
  });
  const [showFilters, setShowFilters] = React.useState(false);
  const [showConfigure, setShowConfigure] = React.useState(false);

  const skipCloseOnNextFilterChangeRef = React.useRef(false);

  const setFilters = React.useCallback<React.Dispatch<React.SetStateAction<TFilters>>>((next) => {
    skipCloseOnNextFilterChangeRef.current = false;
    setFiltersState(next);
  }, []);

  const clearFilters = React.useCallback(() => {
    setFilters(defaultFilters);
    setCurrentPage(1);
  }, [defaultFilters, setFilters]);

  /** Apply a first-load filter seed without treating it as a user filter change. */
  const primeFilters = React.useCallback<React.Dispatch<React.SetStateAction<TFilters>>>((next) => {
    skipCloseOnNextFilterChangeRef.current = true;
    setFiltersState(next);
  }, []);

  React.useEffect(() => {
    onFiltersChange?.(filters);
  }, [filters, onFiltersChange]);

  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  const [internalExpandedRowId, setInternalExpandedRowId] = React.useState<string | null>(null);

  const expandedRowId = manageExpandedRowId
    ? internalExpandedRowId
    : (externalExpandedRowId ?? null);

  const setExpandedRowId = React.useCallback(
    (id: string | null) => {
      if (manageExpandedRowId) {
        setInternalExpandedRowId(id);
      }
    },
    [manageExpandedRowId],
  );

  // Close the detail panel whether the hook or the parent owns expandedRowId.
  const closeExpandedRow = React.useCallback(() => {
    if (manageExpandedRowId) {
      setInternalExpandedRowId(null);
    } else {
      onCloseDetails?.();
    }
  }, [manageExpandedRowId, onCloseDetails]);

  const {
    filterVisibility,
    visibleFilterIds,
    allowedFilterIds,
    handleToggleFilterVisibility,
    isSavingFilterVisibility,
    interfaceElements,
  } = useFilterVisibility<FID>({
    preferencesName: tableName,
    elementsName: tableName,
    allFilterGroupIds,
  });

  const containerRef = React.useRef<HTMLDivElement>(null);
  const expandedRowRef = React.useRef<HTMLTableRowElement>(null);
  const expandedCollapseRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    sessionStorage.setItem(`dt-state:${tableName}`, JSON.stringify({ page: currentPage, filters }));
  }, [currentPage, filters, tableName]);

  const isHiddenByWrapper = React.useContext(CollapseReplaceHiddenContext);

  React.useEffect(() => {
    if (isHiddenByWrapper) {
      setShowFilters(false);
      setShowConfigure(false);
      closeExpandedRow();
    }
  }, [isHiddenByWrapper, closeExpandedRow]);

  // Close the detail panel on filter change (skip the initial mount and first-load seeds).
  const filterMountRef = React.useRef(true);
  React.useEffect(() => {
    if (filterMountRef.current) {
      filterMountRef.current = false;
      return;
    }
    if (skipCloseOnNextFilterChangeRef.current) {
      return;
    }
    closeExpandedRow();
    // Intentionally `filters` only — ignore `closeExpandedRow` identity churn.
  }, [filters]);

  // Close the detail panel on page change (skip the initial mount).
  const pageMountRef = React.useRef(true);
  React.useEffect(() => {
    if (pageMountRef.current) {
      pageMountRef.current = false;
      return;
    }
    closeExpandedRow();
  }, [currentPage]);

  const prevPageRef = React.useRef(currentPage);
  React.useEffect(() => {
    if (prevPageRef.current !== currentPage) {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      prevPageRef.current = currentPage;
    }
  }, [currentPage]);

  const prevExpandedRowIdRef = React.useRef(expandedRowId);
  React.useEffect(() => {
    if (prevExpandedRowIdRef.current !== expandedRowId && expandedRowId) {
      const scrollToCollapse = () => {
        const targetElement = expandedCollapseRef.current || expandedRowRef.current;
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          setTimeout(scrollToCollapse, 100);
        }
      };
      setTimeout(scrollToCollapse, 300);
    }
    prevExpandedRowIdRef.current = expandedRowId;
    // `expandedItem` is intentional: retry scroll once the async row (and its ref) arrives.
  }, [expandedRowId, expandedItem]);

  const handleViewModeChange = React.useCallback(
    (mode: ViewMode) => {
      if (mode === viewMode) return;
      const prevPerPage = resolveDataTableItemsPerPage(viewMode, layoutWidth, isSection);
      const newPerPage = resolveDataTableItemsPerPage(mode, layoutWidth, isSection);
      const firstItemIndex = (currentPage - 1) * prevPerPage;
      const newPage = Math.max(1, Math.floor(firstItemIndex / newPerPage) + 1);
      setCurrentPage(newPage);
      onCloseDetails?.();
      setViewMode(mode);
    },
    [viewMode, layoutWidth, isSection, currentPage, setViewMode, onCloseDetails],
  );

  return {
    viewMode,
    handleViewModeChange,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    filters,
    setFilters,
    primeFilters,
    clearFilters,
    showFilters,
    setShowFilters,
    showConfigure,
    setShowConfigure,
    filterVisibility,
    visibleFilterIds,
    handleToggleFilterVisibility,
    isSavingFilterVisibility,
    allowedFilterIds,
    interfaceElements,
    deleteId,
    setDeleteId,
    expandedRowId,
    setExpandedRowId,
    closeExpandedRow,
    containerRef,
    expandedRowRef,
    expandedCollapseRef,
  };
}
